import React, { useContext, useState, useEffect } from 'react';
import { Download, Filter, X, Search, Eye, Edit, Trash2, Plus, Upload, History } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import userService from '../../../services/userService';
import ConfirmationModal from '../../../components/ConfirmationModal';
import Pagination from '../../../components/Pagination';


export default function ManageUsers() {
    const navigate = useNavigate();
    const { user, hasRole, authTokens } = useContext(AuthContext);
    const { showError, showSuccess } = useToast();

    const [showFiltersSection, setShowFiltersSection] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [filters, setFilters] = useState({
        firstName: '',
        lastName: '',
        email: '',
        role: ''
    });

    const [users, setUsers] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [isImporting, setIsImporting] = useState(false);

    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedImportFile, setSelectedImportFile] = useState(null);

    const openImportModal = () => {
        setShowImportModal(true);
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        setSelectedImportFile(file);
    };

    const confirmImportFile = async () => {
        if (!selectedImportFile) {
            showError("Veuillez sélectionner un fichier.");
            return;
        }

        setIsImporting(true);

        try {
            const result = await userService.uploadFile(selectedImportFile, authTokens.access);

            if (result.success) {
                showSuccess(`Fichier "${selectedImportFile.name}" importé avec succès !`);
                fetchUsers();
            } else {
                showError(result.error || "Erreur lors de l'importation");
            }
        } catch (error) {
            showError("Erreur lors de l'importation : " + error.message);
        } finally {
            setIsImporting(false);
            setShowImportModal(false);
            setSelectedImportFile(null);
        }
    };

    
    const [pagination, setPagination] = useState({
        count: 0,
        page: 1,
        page_size: 10,
        total_pages: 0
    });
    
    // Delete confirmation modal state
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        user: null,
        isLoading: false
    });
    
    // Clear errors when component unmounts
    useEffect(() => {
    return () => {
        setErrors({});
    };
    }, []);


    useEffect(() => {
        fetchUsers();
    }, [pagination.page, pagination.page_size]);

    const fetchUsers = async () => {
        setIsLoading(true);
        setLoadingMessage('récupération de l\'utilisateur...');
        
        try {
            const pagination_data = {
                page: pagination.page,
                page_size: pagination.page_size,
            };
            
            const result = await userService.getUsersList(authTokens.access, pagination_data);

            if (result.success) {
                setUsers(result.data.results); // Handle both paginated and non-paginated responses
                
                // Update pagination info if available
                if (result.data.count !== undefined) {
                    setPagination(prev => ({
                        ...prev,
                        count: result.data.count,
                        total_pages: result.data.total_pages
                    }));
                }
                console.log(result.data)
            } else {
                console.error("Error:", result.error);
                if(result.status == 500){
                    showError("Erreur du serveur, veuillez réessayer plus tard");
                }
                showError(result.error || 'Erreur lors du chargement des utilisateurs');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            showError('Erreur de connexion');
        } finally {
            setIsLoading(false);
        }
    };

    const roleOptions = ['Admin', 'RiskMananger', 'RiskIdentifier', "CartoViewer"];

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            firstName: '',
            lastName: '',
            email: '',
            role: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
        // Fetch will be triggered by useEffect when pagination changes
        setTimeout(() => fetchUsers(), 0);
    };

    const applyFilters = () => {
        console.log('Applying filters:', filters);
        setPagination(prev => ({ ...prev, page: 1 }));
        // Here you would send the filters to your backend
        setTimeout(() => fetchUsers(), 0);
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newPageSize) => {
        setPagination(prev => ({ 
            ...prev, 
            page_size: newPageSize, 
            page: 1 // Reset to first page
        }));
    };

    const exportPDF = () => {
        console.log('Exporting users to PDF...');
        // PDF export functionality
    };

    const importFromExcelOrCSV = () => {
        console.log("Importing users from Excel or CSV...");

        // Create a hidden file input dynamically
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".xlsx,.xls,.csv"; // Allow Excel and CSV files

        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
                console.log("Selected file:", file.name);
                setIsImporting(true);

                try {
                    // Call your service function
                    const result = await userService.uploadFile(file, authTokens.access);

                    if (result.success) {
                        showSuccess(`Fichier "${file.name}" importé avec succès! ${result.data?.imported_count || ''} utilisateurs ajoutés.`);
                        // Refresh the users list after successful import
                        fetchUsers();
                    } else {
                        console.log(result.error)
                        showError(result.error || "Erreur lors de l'importation du fichier");
                    }
                } catch (error) {
                    console.error("❌ File upload failed:", error);
                    showError("Erreur lors de l'importation: " + error.message);
                } finally {
                    setIsImporting(false);
                }
            }
        };

        input.click(); // Trigger the file picker
    };

    const handleShowFiltersSection = () => {
        setShowFiltersSection(!showFiltersSection);
    };

    const getRoleBadge = (role) => {
        const badgeClasses = {
            'Administrateur': 'bg-red-100 text-red-800',
            'Gestionnaire': 'bg-blue-100 text-blue-800',
            'Utilisateur': 'bg-green-100 text-green-800'
        };
        
        return `px-2 py-1 text-xs font-medium rounded-full ${badgeClasses[role] || 'bg-gray-100 text-gray-800'}`;
    };

    const getStatusBadge = (status) => {
        const badgeClasses = {
            'Active': 'bg-green-100 text-green-800',
            'InActive': 'bg-red-100 text-red-800'
        };
        
        return `px-2 py-1 text-xs font-medium rounded-full ${badgeClasses[status] || 'bg-gray-100 text-gray-800'}`;
    };
    const handleEditUser = (user) => {
        // Navigate to edit page with risk ID
        navigate(`/dashboard/admin/users/edit/${user.id}`, { state: { user } });
    };

    const handleViewUser = (user) => {
        // Navigate to user details page
        navigate(`/dashboard/admin/users/${user.id}`);
    };

    const handleViewUserHistory = (user) => {
        navigate(`/dashboard/admin/detailedLogs/user/${user.id}`);
    };



    const canViewHistory = () => {
        return hasRole("Admin");
    };

    const handleDeleteUser = (user) => {
        setDeleteModal({
            isOpen: true,
            user: user,
            isLoading: false
        });
    };

    const confirmDeleteUser = async () => {
        if (!deleteModal.user) return;

        setDeleteModal(prev => ({ ...prev, isLoading: true }));

        try {
            const result = await userService.deleteUser(deleteModal.user.id, authTokens.access);

            if (result.success) {
                showSuccess('Utilisateur supprimé avec succès');
                // Refresh the users list
                const usersResult = await userService.getUsersList(authTokens.access);
                if (usersResult.success) {
                    setUsers(usersResult.data);
                }
                setDeleteModal({ isOpen: false, user: null, isLoading: false });
            } else {
                showError(result.error || 'Erreur lors de la suppression');
                setDeleteModal(prev => ({ ...prev, isLoading: false }));
            }
        } catch (error) {
            showError('Erreur lors de la suppression');
            setDeleteModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    const cancelDeleteUser = () => {
        setDeleteModal({ isOpen: false, user: null, isLoading: false });
    };


    const canViewManageUsers = () => hasRole("Admin");
    if (!canViewManageUsers()) {
        navigate(-1)
        return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="text-yellow-800">
            <h3 className="font-medium">Accès restreint</h3>
            <p className="text-sm mt-1">
                Désolé, vous n'avez pas les autorisations pour afficher cette page
            </p>
            </div>
        </div>
        );
    }


    return (
        <div className="space-y-6">
            {/* Header with Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-900"></h2>

                <div className="flex flex-wrap gap-2">
                    <button
                    onClick={handleShowFiltersSection}
                    className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 hover:shadow-md transition-all duration-200"
                    >
                    <Filter className="h-4 w-4 mr-2" />
                    Filtres
                    </button>
                    {user.organization['canAddUsers'] && (
                        <div className='flex items-center gap-2 '>
                            <button
                                onClick={openImportModal}
                                disabled={isImporting}
                                className="flex items-center px-4 py-2 rounded-lg text-white bg-orange-500 hover:bg-orange-600"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Importer Excel / CSV
                            </button>
                            <Link
                                to="/dashboard/admin/users/add"
                                // to="/dashboard/risks/add"
                                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 hover:shadow-md transition-all duration-200"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Ajouter utilisateur
                            </Link>
                        </div>
                    )}
                </div>
            </div>


            {/* Filters Section */}
            {showFiltersSection && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className='flex items-center justify-between'>
                        <div className="flex items-center mb-4">
                            <Filter className="h-5 w-5 text-gray-500 mr-2" />
                            <h3 className="text-lg font-medium text-gray-900">Filtres</h3>
                        </div>
                        <button onClick={handleShowFiltersSection} className="text-gray-500 hover:text-gray-700">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        {/* First Name Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Prénom
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={filters.firstName}
                                    onChange={(e) => handleFilterChange('firstName', e.target.value)}
                                    placeholder="Rechercher par prénom..."
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Last Name Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nom de famille
                            </label>
                            <input
                                type="text"
                                value={filters.lastName}
                                onChange={(e) => handleFilterChange('lastName', e.target.value)}
                                placeholder="Rechercher par nom..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                        </div>

                        {/* Email Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                value={filters.email}
                                onChange={(e) => handleFilterChange('email', e.target.value)}
                                placeholder="Rechercher par email..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                        </div>

                        {/* Role Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rôle
                            </label>
                            <select
                                value={filters.role}
                                onChange={(e) => handleFilterChange('role', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            >
                                <option value="">Tous les rôles</option>
                                {roleOptions.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Filter Actions */}
                    <div className="flex space-x-3">
                        <button
                            onClick={applyFilters}
                            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors duration-200"
                        >
                            <Filter className="h-4 w-4 mr-2" />
                            Appliquer les filtres
                        </button>
                        <button
                            onClick={clearFilters}
                            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200"
                        >
                            <X className="h-4 w-4 mr-2" />
                            Effacer les filtres
                        </button>
                    </div>
                </div>
            )}

            {/* Table Section */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden relative">
                {/* Loading Overlay */}
                {(isLoading || isImporting) && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <div className="flex flex-col items-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mb-2"></div>
                            <p className="text-sm text-gray-600">
                                {isImporting ? 'Importation en cours...' : loadingMessage}
                            </p>
                        </div>
                    </div>
                )}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Nom et prénom
                                </th>
                         
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type d'utilisateur
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    utilisateur d'audit
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Statut
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date de création
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {user.first_name} {user.last_name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-500">
                                            {user.email}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            {user.roles && user.roles.length > 0 ? (
                                                user.roles.length === 1 ? (
                                                <span className={getRoleBadge(user.roles[0])}>
                                                    {user.roles[0]}
                                                </span>
                                                ) : (
                                                <div className="relative group inline-block">
                                                    <span className={getRoleBadge(user.roles[0])}>
                                                    {user.roles[0]}...
                                                    </span>
                                                    {/* Tooltip */}
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                                    {user.roles.join(", ")}
                                                    </div>
                                                </div>
                                                )
                                            ) : (
                                                <span className="text-gray-400 italic">No Role</span>
                                            )}
                                    </td>

                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {user.is_audit_user? "Oui": "Non"}
                                    </td>   
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={getStatusBadge(user.is_active? "Active": "InActive")}>
                                            {user.is_active? "Active": "InActive"}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                        <button 
                                            onClick={() => handleViewUser(user)}
                                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
                                            title="Voir détails"
                                        >
                                            <Eye className="h-4 w-4" />
                                        </button>
                                        {canViewHistory() && (
                                            <button 
                                                onClick={() => handleViewUserHistory(user)}
                                                className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 rounded transition-colors"
                                                title="Voir l'évolution complète"
                                            >
                                                <History className="h-4 w-4" />
                                            </button>
                                        )}
                                        <button 
                                            onClick={() => handleEditUser(user)}
                                            className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-colors"
                                            title="Modifier"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </button>
                                        {/* {hasRole('Admin') && (
                                            <button 
                                                onClick={() => handleDeleteUser(user)}
                                                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors"
                                                title="Supprimer"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )} */}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.total_pages}
                    totalItems={pagination.count}
                    pageSize={pagination.page_size}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    loading={isLoading}
                    itemName="Utilisateurs" 
                />

                {/* Empty State */}
                {users.length === 0 && !isLoading && (
                    <div className="text-center py-12">
                        <div className="text-gray-500">
                            <p className="text-lg font-medium">Aucun utilisateur trouvé</p>
                            <p className="mt-1">Essayez de modifier vos filtres ou ajoutez de nouveaux utilisateurs.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={cancelDeleteUser}
                onConfirm={confirmDeleteUser}
                title="Supprimer l'utilisateur"
                message="Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible."
                itemName={deleteModal.user ? `${deleteModal.user.first_name} ${deleteModal.user.last_name} (${deleteModal.user.email})` : ''}
                isLoading={deleteModal.isLoading}
                confirmText="Supprimer"
                cancelText="Annuler"
            />

            {/* Import Modal */}
            {showImportModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">

                <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <Upload className="h-5 w-5 mr-2" /> Structure du fichier à importer
                </h2>

                <p className="text-gray-600 mb-3">
                    Votre fichier Excel/CSV doit respecter exactement cette structure :
                </p>

                <p>Example: </p>

                {/* Table preview */}
                <div className="overflow-x-auto mb-4">
                    <table className="text-sm border border-gray-300 w-full">
                        <thead>
                            <tr className="bg-gray-100 border-b">
                                <th className="p-2 border-r">FirstName</th>
                                <th className="p-2 border-r">LastName</th>
                                <th className="p-2 border-r">Contact</th>
                                <th className="p-2 border-r">Email</th>
                                <th className="p-2">RoleFunction</th>
                                <th className="p-2">UserType</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='border-b'>
                                <td className="p-2 border-r">John</td>
                                <td className="p-2 border-r">Doe</td>
                                <td className="p-2 border-r">672527120</td>
                                <td className="p-2 border-r">johndoe@gmail.com</td>
                                <td className="p-2 border-r">QHSE</td>
                                <td className="p-2 border-r">audit</td>
                            </tr>
                            <tr className='border-b'>
                                <td className="p-2 border-r">Adam</td>
                                <td className="p-2 border-r">Smith</td>
                                <td className="p-2 border-r">672527120</td>
                                <td className="p-2 border-r">admasmith@gmail.com</td>
                                <td className="p-2 border-r">Superviseur Terrain</td>
                                <td className="p-2 border-r">risk</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* File select */}
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Sélectionnez un fichier :</label>
                    <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="border p-2 rounded w-full"
                    />
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3">
                    <button
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                    >
                    Annuler
                    </button>

                    <button
                    onClick={confirmImportFile}
                    disabled={isImporting || !selectedImportFile}
                    className={`px-4 py-2 rounded text-white ${
                        isImporting || !selectedImportFile
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-teal-600 hover:bg-teal-700"
                    }`}
                    >
                    {isImporting ? "Importation..." : "Importer"}
                    </button>
                </div>
                </div>
            </div>
            )}

        </div>
    );
}